import {
  ButtonClasses,
  buttonClasses,
  Button as MUIButton
} from "@mui/material"
import React, { ReactElement, ReactNode, forwardRef, useEffect } from "react"
import { FaIcon } from "../../core/icons"
import { useAnnounce } from "../AnnounceProvider"
import { useStyles } from "./styles"

export type ButtonVariant = "text" | "contained" | "outlined"

export type ButtonFocusRing = "default" | "onDark" | "withGap" | "twoTone"

const FOCUS_RING_CLASS = {
  default: "focusRingDefault",
  onDark: "focusRingOnDark",
  withGap: "focusRingWithGap",
  twoTone: "focusRingTwoTone"
} as const

export type Props = {
  id?: string
  size?: "xs" | "sm" | "md" | "lg"
  variant?: ButtonVariant
  className?: string
  startIcon?: ReactElement
  endIcon?: ReactElement
  children: ReactNode[] | ReactNode
  color?:
    | "primary"
    | "secondary"
    | "success"
    | "error"
    | "info"
    | "warning"
    | "white"
    | "inherit"
    | "persistent"
  bold?: boolean
  /**
   * a11y: overrides the focus ring this button would pick from its variant.
   * Saturated `contained` variants already default to `"withGap"`; set this for
   * a surface those defaults can't see — `"onDark"` on a dark background, or
   * `"twoTone"` on a variable/branded one such as a subject banner.
   */
  focusRing?: ButtonFocusRing
  type?: "button" | "submit" | "reset"
  form?: string
  onClick?: (e?: React.MouseEvent<HTMLElement, MouseEvent>) => void | unknown
  onKeyDown?: (e?: React.KeyboardEvent<HTMLElement>) => void | unknown
  onKeyUp?: React.KeyboardEventHandler<HTMLElement>
  onFocus?: React.FocusEventHandler<HTMLElement>
  onBlur?: React.FocusEventHandler<HTMLElement>
  disabled?: boolean
  /**
   * a11y-disabled: renders the button as disabled (styling + `aria-disabled`)
   * and no-ops its handlers, but keeps it focusable and in the a11y tree — unlike
   * native `disabled`, which removes it from the tab order (so it can't be a
   * focus-restore target or be announced). Prefer this over `disabled`.
   */
  isDisabled?: boolean
  /**
   * Shows a spinner and announces `a11yLoadingAnnouncement`. Visual/status only — it
   * does NOT block interaction. To make a control non-interactive while loading, also
   * pass `isDisabled={loading}` (aria-disabled + guarded handlers, focusable, never
   * native `disabled`).
   */
  loading?: boolean
  /**
   * a11y: when the button enters its `loading` state, this text is announced via a
   * polite `role="status"` region so screen-reader users hear what is loading
   * (e.g. "Loading ACT Math practice test"). No-op when `loading` is false/unset.
   */
  a11yLoadingAnnouncement?: string
  "aria-label"?: string
  "data-automation-id"?: string
  title?: string
  component?: React.ElementType
  componentProps?: React.ComponentProps<Props["component"]>
  href?: string
  download?: string
  classes?: Partial<ButtonClasses>
  style?: React.CSSProperties
  disableRipple?: boolean
  role?: React.AriaRole
}

/**
### MasteryPrep Button Component

#### Props:

- `size: 'xs' | 'sm' | 'md' | 'lg'` – specifies sizing styles according to MasteryPrep design styleGuide
<br/>
- `variant: 'text' | 'contained' | 'outlined'` – default MUI Button variant prop
<br/>
- `className: string` – styling classes to inject into button
<br/>
- `startIcon: ReactElement` – MUI attribute for icon before text
<br/>
- `endIcon: ReactElement` – MUI attribute for icon after text
<br/>
- `children: ReactNode[] | ReactNode` – used for content text or components inside button
<br/>
- `color: "primary" | "secondary" | "success" | "error" | "info" | "warning" | "inherit"` - Main color configuration according to design styleGuide
<br/>
- `bold: boolean` - Effra Medium font for buttons with `variant="text"`
<br/>
- `type: "button"|"submit"|"reset" - Button type`
<br/>
- `onClick: () => void | unknown` - onClick function to trigger
<br/>

#### Example Usage:
```

  <Button
    variant="contained"
    color="primary"
    size="xs"
    startIcon={<DeleteIcon/>}
  >
    Delete
  </Button>


```
<br/>
*/
const Button = forwardRef<HTMLButtonElement, Props>(
  (
    {
      id,
      size = "md",
      variant = "text",
      type = "button",
      className,
      children,
      startIcon,
      endIcon,
      color,
      bold,
      focusRing,
      form,
      href,
      onClick,
      onKeyDown,
      disabled,
      isDisabled,
      loading,
      a11yLoadingAnnouncement,
      "data-automation-id": dataAutomationId,
      component,
      componentProps = {},
      classes: outerClassesMap,
      style,
      ...props
    },
    ref
  ) => {
    const { classes, cx } = useStyles()
    const announce = useAnnounce()
    // a11y: announce what is loading through the app-wide live region while loading
    useEffect(() => {
      if (loading && a11yLoadingAnnouncement) announce(a11yLoadingAnnouncement)
    }, [loading, a11yLoadingAnnouncement, announce])
    // a11y: `isDisabled` (not `loading`) is what makes the button non-interactive —
    // aria-disabled + guarded handlers, focusable, never native `disabled` (which drops
    // it from the tab order). `loading` is visual only; pass `isDisabled={loading}` to
    // block interaction while loading.
    const handleClick = (e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (isDisabled) {
        // aria-disabled doesn't block default actions; stop form submit / href navigation
        e?.preventDefault()
        return
      }
      onClick?.(e)
    }
    const handleKeyDown = (e?: React.KeyboardEvent<HTMLElement>) => {
      if (isDisabled) return
      onKeyDown?.(e)
    }
    const styledStartIcon =
      (startIcon || loading) &&
      React.cloneElement(
        loading ? <FaIcon solid spin name="spinner-third" /> : startIcon,
        { style: { fontSize: "inherit" } }
      )
    const styledEndIcon =
      endIcon && React.cloneElement(endIcon, { style: { fontSize: "inherit" } })

    return (
      <MUIButton
        ref={ref}
        id={id}
        disabled={disabled}
        aria-disabled={isDisabled}
        color={
          color === "white" || color === "persistent" ? undefined : color
        }
        startIcon={styledStartIcon}
        endIcon={styledEndIcon}
        variant={variant}
        disableRipple
        href={href}
        className={cx(
          classes.button,
          classes[size],
          classes[`${variant}_${color ?? "default"}`],
          focusRing && classes[FOCUS_RING_CLASS[focusRing]],
          {
            [classes.bold]: !!bold || variant !== "text",
            [buttonClasses.disabled]: isDisabled
          },
          className
        )}
        classes={outerClassesMap}
        style={style}
        type={type}
        form={form}
        data-automation-id={dataAutomationId}
        component={component}
        {...componentProps}
        {...props}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        {children}
      </MUIButton>
    )
  }
)

export default Button
